<script>
  import { onMount } from 'svelte';
  import {
    COLORS, buildScales, clearCanvas, drawGrid, drawTimeLabels,
    drawCrosshair, indexFromX, summarize,
  } from './chartUtils.js';

  // Annualized rolling Sharpe: [{ time, value }] where value is a unitless ratio (null on flat windows).
  export let data = [];

  let canvas;
  let ctx;
  let container;
  let width = 600;
  let height = 200;
  let hoverIndex = -1;

  // Sharpe is a ratio, not a percent â format as a plain number.
  const fmt = (v) => (v === null || v === undefined || Number.isNaN(v) ? 'â' : Number(v).toFixed(2));
  // Drop null windows (zero-std) so the line doesn't break on a gap.
  $: series = (data || []).filter((p) => p && p.value !== null && p.value !== undefined);
  $: hasData = series.length >= 2;
  $: summary = summarize(series, 'Rolling Sharpe', fmt);

  function resize() {
    if (!container) return;
    width = Math.max(200, container.offsetWidth);
    height = 200;
  }

  function draw() {
    if (!ctx || !hasData) return;
    clearCanvas(ctx, width, height);
    const scales = buildScales(series, width, height);
    drawGrid(ctx, width, height, scales, fmt);
    drawTimeLabels(ctx, series, height, scales);

    // Zero baseline (Sharpe crosses zero meaningfully).
    const zeroY = scales.yOf(0);
    if (zeroY >= 0 && zeroY <= height) {
      ctx.strokeStyle = COLORS.label;
      ctx.globalAlpha = 0.4;
      ctx.beginPath();
      ctx.moveTo(scales.xOf(0), zeroY);
      ctx.lineTo(scales.xOf(series.length - 1), zeroY);
      ctx.stroke();
      ctx.globalAlpha = 1;
    }

    ctx.strokeStyle = COLORS.line;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    series.forEach((p, i) => {
      const x = scales.xOf(i);
      const y = scales.yOf(p.value);
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    ctx.stroke();

    if (hoverIndex >= 0) drawCrosshair(ctx, series, hoverIndex, width, height, scales, fmt);
  }

  $: if (ctx && canvas && hasData && width && height && series && hoverIndex >= -1) draw();

  function onMove(e) {
    if (!hasData) return;
    const rect = canvas.getBoundingClientRect();
    hoverIndex = indexFromX(e.clientX - rect.left, series.length, width);
  }
  function onLeave() { hoverIndex = -1; }

  let liveMsg = '';
  function onKey(e) {
    if (!hasData) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const start = hoverIndex < 0 ? 0 : hoverIndex;
      hoverIndex = Math.max(0, Math.min(series.length - 1, start + (e.key === 'ArrowRight' ? 1 : -1)));
      const p = series[hoverIndex];
      liveMsg = `${new Date(p.time).toLocaleDateString()}: ${fmt(p.value)}`;
    }
  }

  // Lazy 2D-context acquisition: the canvas renders inside {#if hasData}, so it may not exist at
  // mount (empty data — acquiring unconditionally threw and broke the whole reactive graph) and may
  // APPEAR later when data arrives. Acquire the context whenever the canvas binds.
  $: if (canvas && !ctx) { ctx = canvas.getContext('2d'); resize(); }

  onMount(() => {
    if (!canvas) return; // not rendered (empty state) — nothing to wire up
    if (canvas) ctx = canvas.getContext('2d');
    resize();
    draw();
    const onResize = () => { resize(); draw(); };
    window.addEventListener('resize', onResize);
    let pendingDraw;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && ctx && canvas) {
          clearTimeout(pendingDraw);
          pendingDraw = setTimeout(() => { resize(); draw(); }, 50);
        }
      });
    }, { threshold: 0.1 });
    if (container) observer.observe(container);
    return () => { window.removeEventListener('resize', onResize); observer.disconnect(); clearTimeout(pendingDraw); };
  });
</script>

<div class="bt-chart" bind:this={container}>
  <h4 class="bt-chart-title">Rolling Sharpe (annualized)</h4>
  {#if hasData}
    <canvas
      bind:this={canvas}
      width={width}
      height={height}
      tabindex="0"
      aria-label={summary}
      on:mousemove={onMove}
      on:mouseleave={onLeave}
      on:keydown={onKey}
    ></canvas>
    <div class="bt-chart-live" aria-live="polite">{liveMsg}</div>
    <details class="bt-chart-data">
      <summary>View data as table</summary>
      <table>
        <thead><tr><th>Time</th><th>Sharpe</th></tr></thead>
        <tbody>
          {#each series as p}<tr><td>{new Date(p.time).toLocaleDateString()}</td><td>{fmt(p.value)}</td></tr>{/each}
        </tbody>
      </table>
    </details>
  {:else}
    <div class="empty-state"><p class="empty-state-description">Insufficient data for this chart.</p></div>
  {/if}
</div>

<style>
  @import './charts.css';
</style>
